import { dispatchAction } from '@ardoq/rxbeach';
import { clearAndSetLoadedState } from 'loadedState/actions';
import { requestMainView } from 'streams/views/mainContent/requestMainView';
import {
  applyFormattingAndLabelRules,
  applyGroupingRules,
} from 'traversals/applyViewpointPerspectives';
import { applyViewpointViewSettings } from 'traversals/applyViewpointViewSettings';
import {
  APIViewpointAttributes,
  GroupingRule,
  LoadedState,
  SYMBOL_TRANSACTION_ID,
  ViewIds,
  WithTransactionId,
} from '@ardoq/api-types';
import { newHistoryEntryRequested } from 'canvas/contextToolBar/loadedStateHistory/loadedStateHistoryActions';
import { isViewpointModeView } from 'views/environmentals/viewSettings/utils';
import { toRenderedViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { v4 as uuid } from 'uuid';
import { ChangesToAppViewState } from './rebuildStateFromPoppedHistoryUtils';

export type SetApplicationViewStateParams = WithTransactionId & {
  conditionalFormatting: APIViewpointAttributes['conditionalFormatting'];
  groupBys: GroupingRule[];
  loadedStates: LoadedState[];
  viewNameCandidate: ViewIds;
  /**
   * Set view settings to null if you don't have valid settings at hand.
   * If null, the view settings get set correctly in the reducer.
   * All view settings are in the current history stream.
   */
  viewSettings: Partial<Record<ViewIds, Record<string, any>>> | null;
};

export const setApplicationViewState = (
  {
    conditionalFormatting,
    groupBys,
    loadedStates,
    viewNameCandidate,
    viewSettings,
    transactionId = uuid(),
  }: SetApplicationViewStateParams,
  {
    hasConditionalFormattingChanged,
    hasGroupBysChanged,
    hasLoadedStateChanged,
    hasViewSettingsChanged,
  }: ChangesToAppViewState | undefined = {
    hasConditionalFormattingChanged: true,
    hasGroupBysChanged: true,
    hasLoadedStateChanged: true,
    hasViewNameChanged: true,
    hasViewSettingsChanged: true,
    hasChanges: true,
  }
) => {
  const conditionalFormattingWithTransactionId = {
    rules: conditionalFormatting,
    transactionId,
  };
  const groupingRulesWithTransactionId = {
    groupingRules: groupBys,
    transactionId,
  };
  const viewName = isViewpointModeView(viewNameCandidate)
    ? viewNameCandidate
    : ViewIds.BLOCKS;
  const viewSettingsWithTransactionId = {
    [viewName]: {
      ...viewSettings?.[viewName],
      [SYMBOL_TRANSACTION_ID]: transactionId,
    },
  };
  // The newHistoryEntryRequested will create a new history entry in the history
  // stream with the given transaction id and view name. All the actual state
  // updates with the same transaction id will then be subsumed in that history
  // entry, meaning they will result in a history replace instead of a history
  // push.
  dispatchAction(newHistoryEntryRequested({ viewName, transactionId }));

  if (hasLoadedStateChanged) {
    dispatchAction(
      clearAndSetLoadedState({
        loadedStates,
        transactionId,
        viewName: viewIdToUserFriendlyName[viewName],
      })
    );
  }

  requestMainView({
    activeTabId: toRenderedViewpointViewId(viewName),
    isViewpointMode: true,
  });

  if (hasViewSettingsChanged) {
    applyViewpointViewSettings({
      viewSettings: viewSettingsWithTransactionId,
      viewName,
    });
  }

  if (hasGroupBysChanged) {
    applyGroupingRules(groupingRulesWithTransactionId);
  }

  if (hasConditionalFormattingChanged) {
    applyFormattingAndLabelRules(conditionalFormattingWithTransactionId);
  }
};
